import React from 'react'
import NavbarOne from './separate/NavbarOne'
import FooterHome from './separate/FooterHome'
import { Link, useNavigate } from 'react-router-dom'
import {
  Crown,
  Clock,
  Users,
  Wallet,
  Shield,
  Zap,
  TrendingUp,
  Headphones,
  ArrowRight,
  Mail,
  Star,
  BarChart3,
  Lock,
  Phone,
  MessageSquare,
  Gem,
} from 'lucide-react'
import './VIPTrading.css'
import PageTitleDes from '../core/support/PageTitleDes'

const vipBenefits = [
  {
    icon: Clock,
    title: '24/7 Instant Access',
    description:
      'Round-the-clock trading with zero downtime. Execute trades anytime, anywhere with priority processing.',
  },
  {
    icon: Users,
    title: 'Dedicated Relationship Manager',
    description:
      'Your personal trading expert available via direct line for market insights and trade execution support.',
  },
  {
    icon: Wallet,
    title: 'Large Portfolio Management',
    description:
      'Effortlessly manage portfolios of any size with advanced tools and personalized strategies.',
  },
  {
    icon: Zap,
    title: 'Priority Execution',
    description:
      'Skip the queue with priority order processing and faster settlement times.',
  },
  {
    icon: Shield,
    title: 'Enhanced Security',
    description:
      'Multi-layer security protocols with dedicated cold storage for VIP accounts.',
  },
  {
    icon: TrendingUp,
    title: 'Exclusive Market Access',
    description:
      'Early access to new listings, exclusive trading pairs, and pre-market opportunities.',
  },
]

const privilegeAccess = [
  {
    icon: Phone,
    title: 'Direct Line Access',
    description:
      'Personal hotline to your dedicated team for instant support and trade execution assistance.',
    benefits: [
      'Instant callback within 30 seconds',
      'Priority queue bypass',
      'Multi-language support',
    ],
  },
  {
    icon: BarChart3,
    title: 'Premium Analytics',
    description:
      'Advanced market intelligence and real-time insights tailored to your trading strategy.',
    benefits: [
      'Custom market reports',
      'Whale movement alerts',
      'Exclusive research access',
    ],
  },
  {
    icon: Gem,
    title: 'Elite Trading Conditions',
    description:
      'Enjoy the lowest fees, highest limits, and fastest execution speeds in the industry.',
    benefits: [
      'Up to 90% fee discount',
      'Unlimited withdrawal limits',
      'Zero slippage guarantee',
    ],
  },
  {
    icon: MessageSquare,
    title: 'Concierge Services',
    description:
      'White-glove service for all your trading needs, from onboarding to complex transactions.',
    benefits: [
      'Personalized onboarding',
      'Tax documentation support',
      'OTC desk priority',
    ],
  },
]

const supportFeatures = [
  {
    icon: TrendingUp,
    title: 'Real-Time Alerts',
    description: 'Customized price alerts and market notifications',
  },
  {
    icon: Shield,
    title: 'Insurance Coverage',
    description: 'Additional asset protection for VIP portfolios',
  },
  {
    icon: Users,
    title: 'Exclusive Events',
    description: 'VIP-only webinars, meetups, and networking',
  },
  {
    icon: Zap,
    title: 'Fast Track KYC',
    description: 'Expedited verification and account upgrades',
  },
  {
    icon: Star,
    title: 'Beta Features',
    description: 'Early access to new platform features',
  },
  {
    icon: Lock,
    title: 'Private Vault',
    description: 'Dedicated cold storage with enhanced security',
  },
]

export default function VIPTrading(props) {
  const navigate = useNavigate()
  return (
    <div className='vip-page'>
       <PageTitleDes title={`VIP Crypto Trading Services | Priority Support & Benefits`} description={`Unlock exclusive VIP trading benefits with priority support, advanced tools, and premium services designed for high-value crypto traders.`} />
      <NavbarOne setTheme={props.setTheme} theme={props.theme} />

      {/* Hero Section */}
        <section
          className='position-relative overflow-hidden d-flex justify-content-center align-items-center py-2'
          style={{ height: '100%', minHeight: 'calc(88vh - 10px)' }}
        >
          <div>
            {/* Animated Background */}
            <div
              className='position-absolute top-0 start-0 w-100 h-100'
              style={{ pointerEvents: 'none' }}
            >
              <div
                className='hero-bg-glow animate-pulse-glow'
                style={{
                  top: '25%',
                  left: '25%',
                  width: '24rem',
                  height: '24rem',
                  background: 'rgba(29, 160, 148, 0.1)',
                }}
              />
              <div
                className='hero-bg-glow animate-pulse-glow'
                style={{
                  bottom: '25%',
                  right: '25%',
                  width: '20rem',
                  height: '20rem',
                  background: 'rgba(29, 160, 148, 0.05)',
                  animationDelay: '1s',
                }}
              />

              {/* Floating VIP Icons */}
              <div
                className='position-absolute animate-float'
                style={{ top: '10rem', left: '10%', opacity: 0.2 }}
              >
                <Crown size={64} className='text-primary-custom' />
              </div>
              <div
                className='position-absolute animate-float'
                style={{
                  top: '15rem',
                  right: '15%',
                  opacity: 0.2,
                  animationDelay: '2s',
                }}
              >
                <Gem size={48} className='text-primary-custom' />
              </div>
              <div
                className='position-absolute animate-float'
                style={{
                  bottom: '10rem',
                  left: '20%',
                  opacity: 0.2,
                  animationDelay: '1s',
                }}
              >
                <Star size={56} className='text-primary-custom' />
              </div>
            </div>

            <div className='container position-relative z-1'>
              <div className='text-center mx-auto' style={{ maxWidth: '56rem' }}>
                {/* Badge */}
                <div
                  className='d-inline-flex align-items-center gap-2 px-3 py-2 rounded-pill mb-4 animate-fade-in'
                  style={{
                    background: 'rgba(29, 160, 148, 0.1)',
                    border: '1px solid rgba(29, 160, 148, 0.2)',
                  }}
                >
                  <Crown size={16} className='text-primary-custom' />
                  <span
                    className='text-primary-custom'
                    style={{ fontSize: '0.875rem', fontWeight: 500 }}
                  >
                    VIP Trading Services
                  </span>
                </div>

                <h1
                  className='display-3 fw-bold mb-4 animate-slide-up'
                  style={{ color: '#e6edf3' }}
                >
                  Elite Trading for{' '}
                  <span className='gradient-text'>High-Volume</span> Traders
                </h1>

                <p
                  className='lead text-muted-foreground mb-5 mx-auto animate-slide-up'
                  style={{ maxWidth: '42rem', animationDelay: '0.1s' }}
                >
                  Experience premium trading with 24/7 instant access, dedicated
                  relationship managers, and effortless large portfolio management.
                </p>

                <div
                  className='d-flex flex-column flex-sm-row gap-3 justify-content-center animate-slide-up'
                  style={{ animationDelay: '0.2s' }}
                >
                  <div onClick={() => navigate('/help-center')} className='btn-vip'>
                    <Crown size={20} className='me-2' />
                    Apply for VIP
                    <ArrowRight size={16} className='ms-2' />
                  </div>
                  <Link to='/usdt-otc-desk' className='btn-vip-outline'>
                    OTC Desk
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

      {/* Benefits Section */}
      <section className='pt-5 position-relative' style={{paddingBottom:'90px'}}>
        <div className='container'>
          <div className='text-center mb-5'>
            <h2 className='vip-section-title fw-bold mb-3'>
              Exclusive VIP Benefits
            </h2>
            <p
              className='text-muted-foreground mx-auto'
              style={{ maxWidth: '42rem' }}
            >
              Unlock premium features designed for serious traders who demand
              the best
            </p>
          </div>

          <div className='row row-cols-1 row-cols-md-2 row-cols-lg-3 g-4'>
            {vipBenefits.map((benefit, index) => (
              <div key={benefit.title} className='col'>
                <div className='vip-card p-4'>
                  <div className='icon-box'>
                    <benefit.icon size={28} className='text-primary-custom' />
                  </div>
                  <h3 className='h5 fw-bold mb-3' style={{ color: '#e6edf3' }}>
                    {benefit.title}
                  </h3>
                  <p className='text-muted-foreground mb-0'>
                    {benefit.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Privilege Access Section */}
      <section
        style={{ background: 'rgba(255,255,255,0.02)', paddingTop:'80px', paddingBottom:'90px' }}
      >
        <div className='container'>
          <div className='text-center mb-5'>
            <h2 className='vip-section-title fw-bold mb-3'>
              Privileged Access & Support
            </h2>
            <p
              className='text-muted-foreground mx-auto'
              style={{ maxWidth: '42rem' }}
            >
              Experience unparalleled service with exclusive privileges reserved
              for our VIP members
            </p>
          </div>

          <div
            className='row row-cols-1 row-cols-md-2 g-4 mx-auto mt-5'
            style={{ maxWidth: '64rem' }}
          >
            {privilegeAccess.map((item, index) => (
              <div key={item.title} className='col'>
                <div
                  className='vip-card p-4'
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className='icon-box'>
                    <item.icon size={28} className='text-primary-custom' />
                  </div>
                  <h3 className='h5 fw-bold mb-3' style={{ color: '#e6edf3' }}>
                    {item.title}
                  </h3>
                  <p className='text-muted-foreground mb-4'>
                    {item.description}
                  </p>
                  <ul className='list-unstyled mb-0 d-flex flex-column gap-2'>
                    {item.benefits.map((benefit) => (
                      <li
                        key={benefit}
                        className='d-flex align-items-center gap-2 small'
                      >
                        <div
                          style={{
                            width: '6px',
                            height: '6px',
                            borderRadius: '50%',
                            background: '#1da094',
                          }}
                        />
                        <span className='text-muted-foreground'>{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Support Features Grid */}
      <section style={{paddingTop:'80px', paddingBottom:'90px'}}>
        <div className='container'>
          <div className='text-center mb-5'>
            <h2 className='vip-section-title fw-bold mb-3'>Why Choose VIP?</h2>
            <p
              className='text-muted-foreground mx-auto'
              style={{ maxWidth: '42rem' }}
            >
              Premium features and dedicated support that elevate your trading
              experience
            </p>
          </div>

          <div
            className='row row-cols-1 row-cols-sm-2 row-cols-lg-3 g-4 mx-auto mt-5'
            style={{ maxWidth: '72rem' }}
          >
            {supportFeatures.map((feature, index) => (
              <div key={feature.title} className='col'>
                <div className='feature-item h-100'>
                  <div
                    className='d-flex align-items-center justify-content-center mb-3'
                    style={{
                      width: '48px',
                      height: '48px',
                      borderRadius: '12px',
                      background: 'rgba(29, 160, 148, 0.1)',
                    }}
                  >
                    <feature.icon size={24} className='text-primary-custom' />
                  </div>
                  <h3 className='h6 fw-bold mb-2' style={{ color: '#e6edf3' }}>
                    {feature.title}
                  </h3>
                  <p className='small text-muted-foreground mb-0'>
                    {feature.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 24/7 Support Highlight */}
      <section
        className='position-relative overflow-hidden'
        style={{ background: 'rgba(255,255,255,0.02)', paddingTop:'80px', paddingBottom:'90px' }}
      >
        <div
          className='position-absolute top-50 start-50 translate-middle'
          style={{
            width: '800px',
            height: '800px',
            background: 'rgba(29, 160, 148, 0.05)',
            borderRadius: '50%',
            filter: 'blur(60px)',
            pointerEvents: 'none',
          }}
        />

        <div className='container position-relative z-1'>
          <div className='mx-auto' style={{ maxWidth: '64rem' }}>
            <div className='row g-5 align-items-center'>
              {/* Left - Content */}
              <div className='col-md-6'>
                <div
                  className='d-inline-flex align-items-center gap-2 px-3 py-2 rounded-pill mb-4'
                  style={{
                    background: 'rgba(29, 160, 148, 0.1)',
                    border: '1px solid rgba(29, 160, 148, 0.2)',
                  }}
                >
                  <Headphones size={16} className='text-primary-custom' />
                  <span
                    className='text-primary-custom'
                    style={{ fontSize: '0.875rem', fontWeight: 500 }}
                  >
                    Always Available
                  </span>
                </div>

                <h2 className='vip-section-title fw-bold mb-4'>
                  Your Personal Trading Team,{' '}
                  <span className='gradient-text'>24/7</span>
                </h2>

                <p className='lead text-muted-foreground mb-5'>
                  With VIP status, you're never alone. Our dedicated
                  relationship managers provide round-the-clock support for
                  trade execution, market analysis, and portfolio optimization.
                </p>

                <div className='d-flex flex-column gap-3'>
                  {[
                    {
                      icon: Clock,
                      text: '24/7 instant support via direct line',
                    },
                    {
                      icon: BarChart3,
                      text: 'Real-time market insights and alerts',
                    },
                    { icon: Lock, text: 'Secure, encrypted communication' },
                  ].map((item) => (
                    <div
                      key={item.text}
                      className='d-flex align-items-center gap-3'
                    >
                      <div
                        className='d-flex align-items-center justify-content-center'
                        style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '10px',
                          background: 'rgba(29, 160, 148, 0.1)',
                        }}
                      >
                        <item.icon size={20} className='text-primary-custom' />
                      </div>
                      <span className='text-muted-foreground'>{item.text}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Right - Visual */}
              <div className='col-md-6'>
                <div className='position-relative'>
                  <div className='vip-card p-4 rounded-4'>
                    <div className='d-flex align-items-center gap-3 mb-4'>
                      <div
                        className='d-flex align-items-center justify-content-center'
                        style={{
                          width: '64px',
                          height: '64px',
                          borderRadius: '16px',
                          background: 'rgba(29, 160, 148, 0.2)',
                        }}
                      >
                        <Crown size={32} className='text-primary-custom' />
                      </div>
                      <div>
                        <div
                          className='h5 fw-bold mb-0'
                          style={{ color: '#e6edf3' }}
                        >
                          VIP Support
                        </div>
                        <div className='small text-primary-custom'>
                          Online • Average response: 30s
                        </div>
                      </div>
                    </div>

                    <div className='d-flex flex-column gap-3'>
                      <div
                        className='p-3 rounded-3'
                        style={{
                          background: 'rgba(255,255,255,0.03)',
                          border: '1px solid rgba(255,255,255,0.05)',
                        }}
                      >
                        <div className='small text-muted-foreground mb-1'>
                          Last Message
                        </div>
                        <div style={{ color: '#e6edf3' }}>
                          "Your order for 50 BTC has been executed at
                          $67,234.50"
                        </div>
                      </div>

                      <div className='row g-3'>
                        <div className='col-6'>
                          <div
                            className='p-3 rounded-3 text-center'
                            style={{ background: 'rgba(29, 160, 148, 0.1)' }}
                          >
                            <div className='h4 fw-bold text-primary-custom mb-0'>
                              99.9%
                            </div>
                            <div className='small text-muted-foreground'>
                              Satisfaction
                            </div>
                          </div>
                        </div>
                        <div className='col-6'>
                          <div
                            className='p-3 rounded-3 text-center'
                            style={{ background: 'rgba(29, 160, 148, 0.1)' }}
                          >
                            <div className='h4 fw-bold text-primary-custom mb-0'>
                              &lt;30s
                            </div>
                            <div className='small text-muted-foreground'>
                              Response
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className='position-relative overflow-hidden' style={{paddingTop:'80px', paddingBottom:'90px'}}>
        <div
          className='position-absolute top-50 start-50 translate-middle'
          style={{
            width: '600px',
            height: '600px',
            background: 'rgba(29, 160, 148, 0.05)',
            borderRadius: '50%',
            filter: 'blur(60px)',
            pointerEvents: 'none',
          }}
        />

        <div className='container position-relative z-1'>
          <div className='mx-auto text-center' style={{ maxWidth: '64rem' }}>
            <div
              className='d-flex align-items-center justify-content-center mx-auto mb-4 animate-pulse-glow'
              style={{
                width: '80px',
                height: '80px',
                borderRadius: '50%',
                background: 'rgba(29, 160, 148, 0.1)',
              }}
            >
              <Mail size={40} className='text-primary-custom' />
            </div>

            <h2 className='vip-section-title fw-bold mb-4'>
              Ready to Trade Like a VIP?
            </h2>

            <p className='lead text-muted-foreground mb-5'>
              Join our exclusive VIP program and experience trading at its
              finest. Apply today and unlock premium benefits.
            </p>

            <div className='vip-card p-5 mx-auto' style={{ maxWidth: '56rem' }}>
              <div className='d-flex align-items-center justify-content-center gap-2 mb-4'>
                <Mail size={20} className='text-primary-custom' />
                <span
                  className='h5 mb-0 fw-medium'
                  style={{ color: '#e6edf3' }}
                >
                  Contact Email
                </span>
              </div>

              <a
                href='mailto:supports@uniticexchange.com'
                className='d-block gradient-text fw-bold mb-4 text-decoration-none vip-cta-email'
              >
                supports@uniticexchange.com
              </a>

              <div className='d-flex flex-column flex-sm-row gap-3 justify-content-center'>
                <div
                  onClick={() => navigate('/help-center')}
                  className='btn-vip flex-grow-1'
                >
                  Apply for VIP Access
                  <ArrowRight size={16} className='ms-2' />
                </div>
                <Link to='/partnership' className='btn-vip-outline flex-grow-1'>
                  Partnership Program
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section >

      <FooterHome />
    </div >
  )
}
